import AbstractColumnTemplate from "./AbstractColumnTemplate.js";
import ActualsPeriodColumnTemplateSource from "./periodColumnTemplateSource/ActualsPeriodColumnTemplateSource.js";
import DynamicBudgetPeriodColumnTemplateSource from "./periodColumnTemplateSource/DynamicBudgetPeriodColumnTemplateSource.js";
import periodColumnTemplateSourceFactory from "./periodColumnTemplateSource/periodColumnTemplateSourceFactory.js";
import periodTemplateFactory from "../periodTemplateFactory.js";
import RelativePeriodTemplate from "../RelativePeriodTemplate.js";
import specificationFactory from "../../../specification/specificationFactory.js";

/**
 * Abstract period template column.
 *
 * Period column templates hold a reference to their periodTemplate, which
 * specifies how to compute the period when interpreting a template.
 *
 * @param {AbstractPeriodTemplate} [periodTemplate] - Period specification.
 *   Default is {@link RelativePeriodTemplate}
 * @param {AbstractPeriodColumnTemplateSource} [source] - Represents where
 *   the column's data is coming from (e.g., actual accounting data or
 *   budget). Default is {@link ActualsPeriodColumnTemplateSource}
 * @param {boolean} [isEditableBudgetColumn] - True if column is editable for budget values.
 * @param {specification} [defaultDimensionSpecification] - Default dimension
 * specification for instances.
 * @abstract
 */
export default class AbstractPeriodColumnTemplate extends AbstractColumnTemplate {
	constructor({
		periodTemplate = new RelativePeriodTemplate(),
		source = new ActualsPeriodColumnTemplateSource(),
		isEditableBudgetColumn,
		defaultDimensionSpecification,
	} = {}) {
		super(...arguments);
		this._periodTemplate = periodTemplate;
		this._source = source;
		this._isEditableBudgetColumn = isEditableBudgetColumn;
		this._defaultDimensionSpecification = defaultDimensionSpecification;

		this._hasDimensionSpecification = !!defaultDimensionSpecification;
	}

	getPeriodTemplate() {
		return this._periodTemplate;
	}

	setPeriodTemplate(periodTemplate) {
		this._periodTemplate = periodTemplate;
	}

	canEditPeriodTemplateType() {
		return true;
	}

	getDefaultDimensionSpecification() {
		return this._defaultDimensionSpecification;
	}

	setDefaultDimensionSpecification(dimensionSpecification) {
		this._defaultDimensionSpecification = dimensionSpecification;
	}

	isPeriodColumn() {
		return true;
	}

	isActualsColumn() {
		return this._source.isActualsSource();
	}

	isBudgetColumn() {
		return this._source.isBudgetSource();
	}

	isForecastEstimateColumn() {
		return this._source.isForecastEstimateSource();
	}

	isEditableBudgetColumn() {
		return this._isEditableBudgetColumn;
	}

	hasOwnDimensionSpecification() {
		return !!this._hasDimensionSpecification;
	}

	hasFixedPeriodTemplate() {
		return this._periodTemplate.isFixedPeriodTemplate();
	}

	setHasDimensionSpecification(boolean) {
		this._hasDimensionSpecification = boolean;
		if (!this._hasDimensionSpecification) {
			this._defaultDimensionSpecification = null;
		}
	}

	setBudgetColumn(budgetColumn) {
		this._source = budgetColumn
			? new DynamicBudgetPeriodColumnTemplateSource()
			: new ActualsPeriodColumnTemplateSource();
	}

	setEditableBudgetColumn(isEditable) {
		this._isEditableBudgetColumn = isEditable;
	}

	/**
	 * Find the period to use for an interpretation context.
	 */
	getPeriodForContext(context) {
		return this._periodTemplate.getPeriodForContext(context);
	}

	getAccountsForContext(context) {
		return this.getPeriodForContext(context).getAccounts();
	}

	accept(visitor) {
		return visitor.visitAbstractPeriodColumnTemplate(this);
	}

	_interpretationSpec(interpreter) {
		const spec = super._interpretationSpec(...arguments);
		const context = interpreter.getContext();
		const period = this.getPeriodForContext(context);

		spec.period = period;

		spec.source = this._source.instantiate({
			columnTemplate: this,
			context,
			period,
		});

		spec.isEditableBudgetColumn = this._isEditableBudgetColumn;

		spec.dimensionSpecification =
			context.getDimensionSpecificationForTemplate(this) ||
			this._defaultDimensionSpecification;

		spec.dimensionTree = context.getDimensionTree();

		return spec;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.periodTemplate = this._periodTemplate;
		properties.isEditableBudgetColumn = this._isEditableBudgetColumn;

		properties.defaultDimensionSpecification =
			this._defaultDimensionSpecification;

		return { ...properties, ...this._source.serialize() };
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.periodTemplate = periodTemplateFactory.materialize(
			data.periodTemplate,
			context,
		);

		data.defaultDimensionSpecification &&= specificationFactory.materialize(
			data.defaultDimensionSpecification,
		);

		data.source = periodColumnTemplateSourceFactory.materialize(
			data,
			context,
		);
	}
}
